// Rows of the chat message menu, in the order and under the conditions of the
// mobile app (Kjuup-Mobile-App src/components/message/v2/chatGlassModel.js
// buildActionRows + ChatAction.MessageAction "Save"). Keep the two in step.
//
// Each row: { key, label, icon, onPress, danger, filled }. Keys:
//   top row (4 tiles): reply · forward · copy | save · todo
//   rows:              translate · markUnread · select · info · edit · delete

import moment from "moment";

export const MENU_TOP_KEYS = ["reply", "forward", "copy", "save", "todo"];

export const EDIT_WINDOW_MINUTES = 10;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** Sent at most 10 minutes ago (or still local, i.e. not on the server yet). */
export function isWithinEditWindow(chat, now = moment()) {
  if (!chat) return false;
  if (chat.local && !chat.isSent) return true;
  if (!chat.createdAt) return false;
  return now.diff(chat.createdAt, "minutes") <= EDIT_WINDOW_MINUTES;
}

/** A media message the menu can offer "Save" for (album, documents, video, voice). */
export function hasSavableMedia(chat) {
  if (!chat || chat.isDeleted) return false;
  const type = String(chat.messageType || "").toLowerCase();
  if (type === "image") {
    if (Array.isArray(chat.images) && chat.images.some((img) => img && img.fileUrl)) return true;
    return !!chat.fileUrl;
  }
  if (type === "doc") {
    if (Array.isArray(chat.documents) && chat.documents.some((d) => d && d.fileUrl)) return true;
    return !!chat.fileUrl;
  }
  if (type === "video" || type === "audio") return !!(chat.fileUrl || chat.serverFileUrl);
  return false;
}

/**
 * p = { chat, myId, isSelfChat, canWrite, isBookmarked, canBookmark,
 *       canCopy, labels, handlers: { reply, forward, copy, save, todo, translate,
 *       markUnread, select, info, edit, delete } }
 * A row only appears when its handler is given. In a chat without a project
 * "Mark as task" first asks for the project (the caller's `todo` handler).
 */
export function buildChatActionRows(p) {
  const {
    chat = {},
    myId,
    isSelfChat = false,
    canWrite = true,
    isBookmarked = false,
    canBookmark = false,
    canCopy = false,
    labels = {},
    handlers = {},
  } = p || {};
  const fromMe = sameId(chat.senderId, myId);
  const received = !fromMe;
  const withinEditWindow = isWithinEditWindow(chat);
  const type = String(chat.messageType || "").toLowerCase();
  const rows = [];
  const add = (key, icon, label, onPress, extra = {}) => {
    if (typeof onPress !== "function") return;
    rows.push({ key, icon, label, onPress, danger: false, filled: false, ...extra });
  };
  const canMarkUnread = received && !chat.isDeleted && typeof handlers.markUnread === "function";

  if (type === "poll") {
    if (fromMe) {
      if (!isSelfChat) add("info", "info", labels.info, handlers.info);
      if (withinEditWindow) add("delete", "delete", labels.delete, handlers.delete, { danger: true });
    }
    if (canMarkUnread) add("markUnread", "mark_chat_unread", labels.markUnread, handlers.markUnread);
    return rows;
  }

  // Task notice card (chat.taskNotice): answer it, nothing that treats it as plain text
  // (forward, translate, copy, edit, mark as task).
  if (chat.taskNotice) {
    if (canWrite !== false) add("reply", "reply", labels.reply, handlers.reply);
    if (canMarkUnread) add("markUnread", "mark_chat_unread", labels.markUnread, handlers.markUnread);
    if (!chat.isDeleted) add("select", "check_circle", labels.select, handlers.select);
    if (fromMe && !isSelfChat) add("info", "info", labels.info, handlers.info);
    if (withinEditWindow && fromMe) add("delete", "delete", labels.delete, handlers.delete, { danger: true });
    return rows;
  }

  if (canWrite !== false) add("reply", "reply", labels.reply, handlers.reply);
  if (canWrite !== false && type !== "map") add("forward", "forward", labels.forward, handlers.forward);
  if (received) {
    add("translate", "translate", labels.translate, handlers.translate);
    if (canMarkUnread) add("markUnread", "mark_chat_unread", labels.markUnread, handlers.markUnread);
  }
  if (!chat.isDeleted) add("select", "check_circle", labels.select, handlers.select);
  if (fromMe && !isSelfChat) add("info", "info", labels.info, handlers.info);
  if (canBookmark) {
    add("todo", isBookmarked ? "task_alt" : "add_task", isBookmarked ? labels.todoRemove : labels.todo, handlers.todo, {
      filled: !!isBookmarked,
    });
  }
  if (canCopy) add("copy", "content_copy", labels.copy, handlers.copy);
  if (withinEditWindow && fromMe) {
    if (canWrite !== false && type === "simple" && !chat.isDeleted) add("edit", "edit", labels.edit, handlers.edit);
    add("delete", "delete", labels.delete, handlers.delete, { danger: true });
  }
  if (!rows.some((r) => r.key === "copy") && hasSavableMedia(chat)) {
    add("save", "download", labels.save, handlers.save);
  }
  return rows;
}

/** Splits the rows into the 4-tile top row and the list below (design order). */
export function splitMenuRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const top = MENU_TOP_KEYS.map((k) => list.find((r) => r.key === k)).filter(Boolean);
  const rest = list.filter((r) => !MENU_TOP_KEYS.includes(r.key));
  return { top, rest };
}
